import React from 'react'

import { PlotBox } from '../components/chart-segment/PlotBox'
import { ChartHeadingGap, ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'
import { TextClamp } from '../components/text/TextClamp'
import { clampArray } from '../foundations/slide-limits'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { insightBulletRhythm, schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { RankingBarsPlot, readRankingSlide } from '../plots/ranking-bars'
import { EditableSlideText } from './EditableSlideText'
import { registerSlide } from './registry'
import { RailLayout } from '../layouts/RailLayout'
import { presetSlot } from '../layouts/presetSlots'
import { SlotBullets, SlotHeadline, SlotNarrative } from '../slots'
import { PLOT_CARD_SURFACE } from '../slots/PlotCard'
import { SlideQuoteCard } from './SlideQuoteCard'
import type { EditableSlideProps } from './contracts'
import type { RankingSlideData } from './renderer-internal-types'
import type { StoredRankingSlide } from '../plots/ranking-bars'

// legacy reader only: the narrative eyebrow is required, so a deck built today stamps it.
const LEGACY_EYEBROW = 'Ranking'

const GRAY = 'var(--slide-text-muted)'
const DARK = 'var(--slide-cover-bg)'
const BRAND_EMPHASIS = 'var(--slide-accent)'
const RANKING_SLIDE_TYPE = 'ranking'
const CHART_TITLE_CLAMP = schemaBackedProseClampProps('chartTitle', RANKING_SLIDE_TYPE, {
  lineHeight: '1.2',
})
const ACTION_SUBTITLE_CLAMP = schemaBackedProseClampProps('actionSubtitle', RANKING_SLIDE_TYPE, {
  lineHeight: '1.5',
})

const HEADLINE = presetSlot(RANKING_SLIDE_TYPE, 'headline').text!

function RankingSlideComponent(props: RankingSlideData & EditableSlideProps & { plot?: StoredRankingSlide['plot'] }) {
  // D17: forwarded to every clamp that offers a truncation tooltip. In a static export
  // (the headless-browser PDF pass) a Radix trigger can never open, so those clamps fall
  // back to the native `title`, which is the only carrier a printed page can hold.
  const { editable, onEdit, renderMode } = props
  const resolvedActionTitle = sanitizeNarrativeText(props.actionTitle)
  const logoUrl = props.clientLogoUrl ?? null
  const bars = clampArray(props.bars || [], 'ranking', 'bars')
  // The chart is the ranking-bars plot (plots plan I): a slide built with
  // `plot` draws Python's ranks, winner, tie, decimals and scale; a slide
  // stored before it draws through the legacy read adapter, as it always did.
  const ranking = readRankingSlide({ type: 'ranking', bars, plot: props.plot })
  const labelFieldPrefix = ranking.isLegacy ? 'bars' : 'plot.data.items'
  const drawnItems = ranking.payload.data.items
  const quote = props.quoteCard
  // One or two bullets take the column's roomier rhythm (see insightBulletRhythm).
  const bulletRhythm = insightBulletRhythm(props.insightBullets?.length ?? 0)

  return (
    <RailLayout
      preset={RANKING_SLIDE_TYPE}
      eyebrow={props.eyebrow || LEGACY_EYEBROW}
      clientLogoUrl={logoUrl}
      testIds={{ layout: 'ranking-slide-layout', plot: 'ranking-slide-panel', rail: 'ranking-slide-narrative' }}
      footerText={props.footerText}
      plotStyle={{
        ...PLOT_CARD_SURFACE,
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      railStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
      plot={
          <PlotBox
            slide={props as unknown as Record<string, unknown>}
            slideType="ranking"
            columns={drawnItems.map((item) => ({ id: item.id, label: item.label }))}
            title={props.chartTitle || props.title || ''}
          >
          <div
            style={{
              height: '100%',
              padding: '40px 32px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
          <ChartSegmentTitleSlot contentPadRight={32} style={{ flexShrink: 0 }}>
          <EditableSlideText
            data-testid="ranking-slide-chart-title"
            value={props.chartTitle || props.title || ''}
            field="chartTitle"
            editable={editable}
            onEdit={onEdit}
            tooltipOnTruncate
            renderMode={renderMode}
            minLines={CHART_TITLE_CLAMP.minLines}
            maxLines={CHART_TITLE_CLAMP.maxLines}
            lineHeight={CHART_TITLE_CLAMP.lineHeight}
            style={{ fontSize: 16, fontWeight: 600, color: 'var(--slide-text-primary)' }}
          />
          </ChartSegmentTitleSlot>
          <ChartHeadingGap height={48} minHeight={16} />
          <RankingBarsPlot
            data={ranking.payload.data}
            size="slide"
            highlight={ranking.highlight}
            renderMode={renderMode}
            formatValue={ranking.formatValue}
            renderLabel={({ item, index, maxLines, style }) => (
              <EditableSlideText
                data-testid="ranking-bar-label"
                value={item.label}
                field={`${labelFieldPrefix}.${index}.label`}
                editable={editable}
                onEdit={onEdit}
                minLines={1}
                maxLines={maxLines}
                lineHeight="1.2"
                chartLabel={{ hintIndex: index }}
                renderMode={renderMode}
                style={style}
              />
            )}
          />

          {props.scoreExplanation ? (
            <TextClamp
              data-testid="ranking-score-explanation"
              minLines={1}
              maxLines={2}
              lineHeight="1.4"
              style={{ fontSize: 13, color: GRAY, marginTop: 16, flexShrink: 0 }}
            >
              {props.scoreExplanation}
            </TextClamp>
          ) : null}
          </div>
          </PlotBox>
      }
    >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36, flexShrink: 0, minWidth: 0 }}>
            <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} editable={editable} onEdit={onEdit} />
            {props.actionSubtitle ? (
              <SlotNarrative
                value={props.actionSubtitle}
                clamp={ACTION_SUBTITLE_CLAMP}
                testId="ranking-slide-action-subtitle"
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
            {props.insightBullets?.length ? (
              <SlotBullets
                bullets={props.insightBullets}
                fontSize={bulletRhythm.fontSize}
                gap={bulletRhythm.gap}
                maxLines={bulletRhythm.maxLines}
                dotColor={BRAND_EMPHASIS}
                testId="ranking-slide-insight-bullets"
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
          </div>

          {quote ? (
            <SlideQuoteCard quoteCard={quote} testId="ranking-slide-quote-card" />
          ) : null}
    </RailLayout>
  )
}

export const RankingSlide = registerSlide('ranking', RankingSlideComponent)
